import { FC, useState, useEffect } from 'react'
import { Input, message } from 'antd'
import {
    IconButton,
    CertificateCard,
    Alert,
    TimeRangePicker,
    ContractCard,
    ThirdPartyToolsCard,
} from '../../../../../ui'
import useRequest from '../../../../../hooks/useRequest'
import { getCertificate, getContract, getOtherPublic } from '../../../../config'
import { monthPicker } from '../../../../../helpers'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectDictionariesMaps } from '../../../../../../store/dictionaries'
import moment from 'moment'
import style from './style.m.less'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { OwnerBlock } from '../../../../../UserCertificate/components'

const Search: FC = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const [toolType, setToolType] = useState(() => {
        if (localStorage.getItem('green_tools_tool_type')) {
            return localStorage.getItem('green_tools_tool_type')
        } else {
            localStorage.setItem('green_tools_tool_type', 'payTypeCertificate')
            return 'payTypeCertificate'
        }
    })
    const [certificateNumber, setCertificateNumber] = useState(() => {
        if (localStorage.getItem('green_tools_certificate_number')) {
            return localStorage.getItem('green_tools_certificate_number')
        } else {
            localStorage.setItem('green_tools_certificate_number', '')
            return ''
        }
    })
    const [deliveryRange, setDeliveryRange] = useState(() => {
        if (localStorage.getItem('green_tools_delivery_range')) {
            const dateArr = JSON.parse(localStorage.getItem('green_tools_delivery_range'))
            if (Array.isArray(dateArr) && dateArr.length == 2) {
                return [moment(dateArr[0]), moment(dateArr[1])]
            } else {
                localStorage.removeItem('green_tools_delivery_range')
                return undefined
            }
        } else {
            localStorage.removeItem('green_tools_delivery_range')
            return undefined
        }
    })
    const { fetch, result, dropState } = useRequest(getCertificate)
    const {
        fetch: fetchContract,
        result: resultContract,
        dropState: dropContract,
    } = useRequest(getContract)
    const {
        fetch: fetchOther,
        result: resultOther,
        dropState: dropOther,
    } = useRequest(getOtherPublic)

    const isCertificate = toolType === 'payTypeCertificate'
    const isContract = toolType === 'payTypeGreenContract'
    const isOther = toolType === 'payTypeExternalTools'

    const options = [
        { label: 'payTypeCertificate', id: '065cdaf1-a0ec-4379-9a5e-b225be191148' },
        { label: 'payTypeGreenContract', id: '7ad9f398-2383-48f6-b2b9-11caad7abb1a' },
        { label: 'payTypeExternalTools', id: 'c6a34b33-03be-4211-bc66-c209c6b52e4f' },
    ]

    const onChange = (value: string) => {
        setToolType(value)
        localStorage.setItem('green_tools_tool_type', value)
        setCertificateNumber('')
        localStorage.removeItem('green_tools_certificate_number')
        setDeliveryRange(undefined)
        localStorage.removeItem('green_tools_delivery_range')
        if (isCertificate) {
            dropState()
        } else if (isContract) {
            dropContract()
        } else {
            dropOther()
        }
    }

    const onChangeCertificateNumber = (e: any) => {
        setCertificateNumber(e.target.value)
        localStorage.setItem('green_tools_certificate_number', e.target.value)
    }

    const { renewableEnergyTypesMap } = useAppSelector(selectDictionariesMaps) ?? {}

    const getEnergyType = (item: number) => renewableEnergyTypesMap?.get(item)

    useEffect(() => {
        if (location.state && location.state.fromMenu) {
            if (isCertificate) {
                dropState()
            } else if (isContract) {
                dropContract()
            } else {
                dropOther()
            }
            setCertificateNumber('')
            localStorage.removeItem('green_tools_certificate_number')
            setDeliveryRange(undefined)
            localStorage.removeItem('green_tools_delivery_range')
            setToolType('payTypeCertificate')
            localStorage.removeItem('green_tools_tool_type')
        } else {
            if (certificateNumber) {
                onFinish()
            }
        }
    }, [toolType])

    const onFinish = () => {
        if (certificateNumber) {
            if (isCertificate) {
                fetch({
                    body: {
                        certificate_number: certificateNumber,
                    },
                    onError: () => dropState(),
                    errorCodeMessage: true,
                })
            } else if (isContract) {
                fetchContract({
                    body: {
                        limit: 10,
                        offset: 0,
                        filters: {
                            contract_number: certificateNumber,
                            ts_delivery_from: deliveryRange
                                ? moment(deliveryRange[0]).startOf('day')
                                : undefined,
                            ts_delivery_to: deliveryRange
                                ? moment(deliveryRange[1]).endOf('day')
                                : undefined,
                        },
                    },
                    onSuccess: (data) => {
                        if (!data.contracts.length) {
                            message.error(t('greenTools.errorGreenContractNotFound'))
                        }
                    },
                    onError: () => dropContract(),
                    errorCodeMessage: true,
                })
            } else {
                fetchOther({
                    body: {
                        ext_instrument_number: certificateNumber,
                    },
                    onSuccess: (data) => {
                        if (data === '') {
                            message.error(t('greenTools.errorExternalToolNotFound'))
                        }
                    },
                    onError: () => dropOther(),
                    errorCodeMessage: true,
                })
            }
        } else {
            if (isContract && !deliveryRange) {
                message.error(t('greenTools.errorEmptyPeriod'))
            } else {
                message.error(t('greenTools.errorEmptyNumber'))
            }
        }
    }

    const createCertificateCard = () => {
        if (isCertificate && result) {
            const {
                certificate_id,
                certificate_number,
                renewable_energy_type_id,
                status_id,
                volume,
                generator_name,
                ts_created,
                is_etp,
                ts_expired,
                month,
                year,
                is_deposit,
                generator_id,
                owner,
            } = result
            const productionPeriod = `${monthPicker(month)} ${year}`
            return (
                <>
                    <CertificateCard
                        toolType={t(`greenTools.${toolType}`)}
                        certificateNumber={certificate_number}
                        option={status_id}
                        isEtp={is_etp}
                        energyType={getEnergyType(renewable_energy_type_id)}
                        capacity={volume}
                        productionPeriod={productionPeriod}
                        generatorName={generator_name}
                        certificateId={certificate_id}
                        generator_id={generator_id}
                        isDeposit={is_deposit}
                        createdTs={moment(ts_created).format('DD.MM.YYYY')}
                        expiredTs={moment(ts_expired).format('DD.MM.YYYY')}
                    />
                    <div className={style.ownerCardMain}>
                        <OwnerBlock title={t('detailedCard.ownerInfo')} ownerInfo={owner} />
                    </div>
                </>
            )
        } else if (isContract && resultContract?.contracts?.length) {
            const totalVolume = resultContract?.contracts?.reduce(
                (acc: number, contract: any) => acc + contract?.volume_new,
                0,
            )
            return (
                <ContractCard
                    isRegistered={false}
                    isOwner
                    isSearchView
                    contract={{ ...resultContract.contracts[0], volume_new: totalVolume }}
                    key={resultContract.contracts[0].contract_id}
                />
            )
        } else if (isOther && resultOther) {
            return (
                <ThirdPartyToolsCard
                    isRegistered={false}
                    isOwner
                    tool={{
                        total_volume: resultOther.volume,
                        number: resultOther.instrument_number,
                        month: moment(resultOther.period).month() + 1,
                        year: moment(resultOther.period).year(),
                        ...resultOther,
                    }}
                    key={resultOther.generator_id}
                />
            )
        }
    }

    const toolWord = isCertificate
        ? t('greenTools.certificate')
        : isContract
        ? t('greenTools.contract')
        : t('greenTools.tool')

    return (
        <>
            <div className={style.selectorWrapper}>
                <div className={style.selectorHeader}>{t('greenTools.searchQuestion')}</div>
                <div className={style.selector}>
                    {options.map((item) => {
                        const { label, id } = item
                        return (
                            <div
                                className={
                                    toolType === label ? style.greenToolSelected : style.greenTool
                                }
                                key={id}
                                onClick={() => onChange(label)}
                            >
                                {t(`greenTools.${label}`)}
                            </div>
                        )
                    })}
                </div>
                {toolType && (
                    <div className={style.numberInputWrapper}>
                        <div>
                            <div>
                                {t('greenTools.numberLabel')} {toolWord}
                            </div>
                            <div className={style.inputBlock}>
                                <Input
                                    value={certificateNumber}
                                    onChange={onChangeCertificateNumber}
                                    className={style.certificateNumberInput}
                                    placeholder={`${t('greenTools.numberPlaceholder')} ${toolWord}`}
                                />
                            </div>
                        </div>
                        {isContract && (
                            <div className={style.leftGapBlock}>
                                <div>{t('greenTools.periodSupply')}</div>
                                <div className={style.inputBlock}>
                                    <TimeRangePicker
                                        onChange={(value) => {
                                            setDeliveryRange(value)
                                            localStorage.setItem(
                                                'green_tools_delivery_range',
                                                JSON.stringify(value),
                                            )
                                        }}
                                        value={deliveryRange}
                                    />
                                </div>
                            </div>
                        )}
                        <div className={style.leftGapBlock}>
                            <IconButton
                                onClick={onFinish}
                                className={style.searchButton}
                                customStyle={{ height: '32px' }}
                            >
                                {t('greenTools.search')}
                            </IconButton>
                        </div>
                    </div>
                )}
                {isCertificate && (
                    <Alert
                        message={
                            <div>
                                {t('greenTools.pledgeAlertStart')}
                                <Link className={style.link} to="/certificates-in-pledge">
                                    {t('greenTools.pledgeAlertEnd')}
                                </Link>
                            </div>
                        }
                        type="info"
                        className={style.alert}
                    />
                )}
            </div>
            <div className={style.card}>{createCertificateCard()}</div>
        </>
    )
}

export default Search
